import { CreditCardProductCategory } from '@appTypes/EligibilityApi.interface'

import {
    StyledFaqContentStack,
    StyledFaqSubHeading,
    StyledFaqText,
} from '@components/FaqAccordion'
import UnorderedList, { ListItem } from '@components/UnorderedList'

import { creditLimitFaq, generalScoredQuestions } from '@config/commonFaqs'

type FaqArrayType = Array<{ answer: React.ReactNode; question: string }>

export const scoredTableFaqContent: Record<
    CreditCardProductCategory,
    FaqArrayType
> = {
    [CreditCardProductCategory.BestCards]: [
        ...generalScoredQuestions,
        {
            question: 'What is credit?',
            answer: (
                <StyledFaqText color="light">
                    Credit is basically borrowing money. A company offers credit
                    that you can spend, then you have to meet the repayment
                    terms. Credit cards, loans, mortgages and overdrafts are all
                    forms of credit. Other credit agreements may include mobile
                    phone contracts and gym memberships.
                </StyledFaqText>
            ),
        },

        creditLimitFaq,

        {
            question: 'How do lenders decide who to lend to?',
            answer: (
                <StyledFaqContentStack>
                    <StyledFaqText>
                        Lenders base their decision about whether to lend to you
                        on several factors, including details on your credit
                        report, home status, and your monthly income.
                    </StyledFaqText>

                    <StyledFaqText>
                        You can improve your chances of acceptance in a few
                        ways. Being responsible with credit, never going over
                        25% of your credit limit and repaying your full balance
                        at the end of each month via direct debit, could help.
                        Similarly, getting on the electoral roll will help too,
                        as it shows you really do live where you claim.
                    </StyledFaqText>
                </StyledFaqContentStack>
            ),
        },

        {
            question: 'What are the different types of credit?',
            answer: (
                <StyledFaqContentStack>
                    <StyledFaqText>
                        There are lots of different types of credit, from credit
                        cards to loans, overdrafts to finance arrangements.
                    </StyledFaqText>

                    <StyledFaqText>
                        There are also lots of different types of credit cards,
                        all for different purposes, from moving a balance from
                        one to other, to spreading payments interest-free, to
                        building credit history.
                    </StyledFaqText>

                    <StyledFaqText>
                        But, don’t make too many applications too quickly, or
                        lenders will be less likely to lend to you. Instead,
                        check your eligibility first on TotallyMoney, so you
                        only make one application.
                    </StyledFaqText>
                </StyledFaqContentStack>
            ),
        },

        {
            question: 'Making the most of your credit card',
            answer: (
                <StyledFaqContentStack>
                    <StyledFaqText>
                        Make the most of your credit card by using it
                        responsibly, making at least the minimum monthly payment
                        every month and not using more than 25% of your limit if
                        you can. That way, you can build your credit score and
                        improve your history.
                    </StyledFaqText>

                    <StyledFaqText>
                        The best way to pay off a credit card each month in full
                        is to set up a direct debit, so it clears automatically.
                        Always set up a direct debit for at least the minimum
                        amount — but it’s better to pay it off in full if you
                        can.
                    </StyledFaqText>

                    <StyledFaqText>
                        Finally, don’t forget to make use of Section 75. This
                        means, if anything goes wrong, you can claim for a
                        refund on any purchase on the card between £100 and
                        £30,000.
                    </StyledFaqText>
                </StyledFaqContentStack>
            ),
        },

        {
            question: 'Avoiding the minimum repayments trap',
            answer: (
                <StyledFaqContentStack>
                    <StyledFaqText>
                        If you only make the minimum repayments on your credit
                        card, it could take you over 25 years to pay the card
                        off. It’s easy to fall into this trap, but don’t be
                        deceived: it’s much better to pay a card off quickly,
                        compared to just making minimum repayments.
                    </StyledFaqText>

                    <StyledFaqContentStack>
                        <StyledFaqSubHeading>
                            What is a minimum repayment?
                        </StyledFaqSubHeading>

                        <StyledFaqText>
                            Credit cards are unique in that you can decide what
                            you repay each month, as long as it’s above the
                            minimum repayment, which is set by law. This tells
                            you the smallest amount you’re required to pay back
                            each month. If you don’t pay, you’ll be fined.
                        </StyledFaqText>
                    </StyledFaqContentStack>

                    <StyledFaqContentStack>
                        <StyledFaqSubHeading>
                            The legal minimum
                        </StyledFaqSubHeading>

                        <StyledFaqText>
                            The minimum repayment on cards varies with lender,
                            but since 1st April 2011, monthly repayments have to
                            be at least 1% of the outstanding balance plus any
                            interest accrued on the balance that month.
                        </StyledFaqText>

                        <StyledFaqText>
                            The repayment will also include any fees and default
                            charges. Also, when you’re close to clearing the
                            balance, lenders usually provide a minimum repayment
                            amount. This is typically between £5 and £25.
                        </StyledFaqText>
                    </StyledFaqContentStack>
                </StyledFaqContentStack>
            ),
        },

        {
            question: 'How to choose the right credit card',
            answer: (
                <StyledFaqContentStack>
                    <StyledFaqText>
                        Whether you want to improve your credit score, spread
                        the cost of a large purchase, transfer a balance —
                        there’s a card for you.
                    </StyledFaqText>

                    <StyledFaqText>
                        You might even be able to get an interest-free offer on
                        some cards, while other cards offer guaranteed on rates,
                        limits, and lengths.
                    </StyledFaqText>

                    <StyledFaqText>
                        See our full guide on how to choose the best credit card
                        for you{' '}
                        <a href="https://www.totallymoney.com/credit-cards/best-credit-cards/">
                            here
                        </a>
                        .
                    </StyledFaqText>

                    <StyledFaqText>
                        Also, don’t forget our Best Match feature can find you
                        the best credit card for you, based on what we know
                        about your individual situation.
                    </StyledFaqText>
                </StyledFaqContentStack>
            ),
        },
    ],

    [CreditCardProductCategory.BalanceTransfer]: [
        ...generalScoredQuestions,

        {
            question: 'What is a balance transfer credit card?',
            answer: (
                <StyledFaqContentStack>
                    <StyledFaqText>
                        A 0% balance transfer card can be an effective way to
                        cut the cost of existing debt. You can transfer an
                        existing card balance across to the new card, meaning
                        you’d stop paying interest on the balance.
                    </StyledFaqText>

                    <StyledFaqText>
                        So, you’d be able to save money, or alternatively pay
                        the debt off sooner by using the money you were
                        previously paying in interest to clear the debt.
                    </StyledFaqText>
                </StyledFaqContentStack>
            ),
        },

        {
            question: 'What will it cost?',
            answer: (
                <StyledFaqText>
                    There is a fee to pay, typically 1–3%, when you move your
                    debt onto a balance transfer card. In most cases, this is
                    less than the interest you’d pay if you didn’t move it over.
                </StyledFaqText>
            ),
        },

        {
            question: 'How do I transfer a balance?',
            answer: (
                <StyledFaqContentStack>
                    <StyledFaqText>
                        Making a balance transfer is incredibly easy. Simply
                        give your new card lender, the one offering you the
                        balance transfer card, the details of the debts you want
                        to transfer, including your old card details and the
                        amount. They will pay off those debts and create a new
                        balance on your 0% card.
                    </StyledFaqText>

                    <StyledFaqText>
                        You usually need to do this within 60–90 days of taking
                        out the new card. Be sure to close your old cards, as
                        having multiple accounts open can affect your credit
                        rating.
                    </StyledFaqText>
                </StyledFaqContentStack>
            ),
        },
    ],
    [CreditCardProductCategory.Cashback]: [
        ...generalScoredQuestions,

        {
            question: 'What’s a cashback credit card?',
            answer: (
                <StyledFaqContentStack>
                    <StyledFaqText>
                        Earn while you spend, with a cashback credit card.
                    </StyledFaqText>

                    <StyledFaqText>
                        Cashback credit cards mean a percentage of the amount
                        spent is paid back to the card holder. If you use a
                        cashback credit card for all of your spending, you could
                        earn hundreds of pounds every year.
                    </StyledFaqText>

                    <StyledFaqText>
                        However, the cashback and interest rates are carefully
                        worked out by the lender. So, if you don’t clear your
                        balance every month, you’ll pay far more in interest
                        than you’ll ever earn in cashback. So, only use a
                        cashback credit card if you know you can pay off the
                        balance in full each month.
                    </StyledFaqText>
                </StyledFaqContentStack>
            ),
        },

        {
            question: 'How do I get the most from my cashback credit card?',
            answer: (
                <StyledFaqContentStack>
                    <StyledFaqText>
                        Used sensibly, a cashback credit card can save you
                        money, but a few slip-ups could wipe out earnings in
                        seconds.
                    </StyledFaqText>

                    <StyledFaqText>
                        To get the most out of it, make sure you know what your
                        card offers you, and use it for all your spending, as
                        long as you can pay the balance off in full every month,
                        ideally with a direct debit.
                    </StyledFaqText>

                    <StyledFaqText>
                        Also, avoid using it to withdraw cash, as you’ll pay
                        interest on it and it’s not eligible for cashback.
                    </StyledFaqText>
                </StyledFaqContentStack>
            ),
        },

        {
            question: 'What’s better: cashback or rewards?',
            answer: (
                <StyledFaqContentStack>
                    <StyledFaqText>
                        Cashback is a type of reward, but that doesn’t mean it’s
                        always the best option.
                    </StyledFaqText>

                    <StyledFaqText>
                        If you fly a lot, a credit card linked to an airline
                        reward program could end up being more worthwhile. Or, a
                        store card for a supermarket could be more beneficial if
                        you do your weekly shop at the same place.
                    </StyledFaqText>

                    <StyledFaqText>
                        Overall, because you need to spend a lot before rewards
                        have any real value, cashback cards are often the better
                        choice for most people.
                    </StyledFaqText>
                </StyledFaqContentStack>
            ),
        },

        {
            question: 'Will I be accepted for a cashback card?',
            answer: (
                <StyledFaqContentStack>
                    <StyledFaqText>
                        While the best cashback deals tend to be reserved for
                        those with the best credit ratings, there are still
                        options available if you have a less than perfect score.
                    </StyledFaqText>

                    <StyledFaqText>
                        Just avoid applying for multiple cards at once — every
                        unsuccessful application leaves a mark on your credit
                        rating. Our free eligibility checker will show you the
                        credit cards that could accept you.
                    </StyledFaqText>
                </StyledFaqContentStack>
            ),
        },
    ],
    [CreditCardProductCategory.Combined]: [
        ...generalScoredQuestions,
        {
            question: 'What are balance and purchase cards?',
            answer: (
                <StyledFaqContentStack>
                    <StyledFaqText>
                        A card with a balance transfer offer and a purchase
                        offer effectively combines two cards into one.
                    </StyledFaqText>

                    <StyledFaqText>
                        Providing the best of both, it offers a combination of
                        an interest-free period on balance transfers and a 0%
                        interest period on new spending.
                    </StyledFaqText>

                    <StyledFaqText>
                        This means that there’s no excuse for paying interest on
                        your existing credit card debt or new purchases. Plus,
                        you’ll only need to apply for one credit card.
                    </StyledFaqText>
                </StyledFaqContentStack>
            ),
        },
    ],
    [CreditCardProductCategory.PoorCredit]: [
        ...generalScoredQuestions,

        {
            question: 'Understanding credit cards for poor credit',
            answer: (
                <StyledFaqContentStack>
                    <StyledFaqText>
                        Poor credit credit cards, also called bad credit credit
                        cards, are designed to help you if you have a poor, or
                        non-existent, credit record.
                    </StyledFaqText>

                    <StyledFaqText>
                        These cards accept people who would be rejected for a
                        mainstream credit card. They are an excellent way of
                        building up a good credit record. If you get one, use it
                        sensibly and pay your balance off in full each month,
                        and your credit score could start going up.
                    </StyledFaqText>

                    <StyledFaqText>
                        However, they carry very high interest rates — typically
                        29–35% APR — so never borrow more money than you can
                        afford to repay.
                    </StyledFaqText>
                </StyledFaqContentStack>
            ),
        },

        {
            question: 'How does the eligibility check work?',
            answer: (
                <StyledFaqContentStack>
                    <StyledFaqText>
                        You can check your eligibility for any card on
                        TotallyMoney as much as you’d like, without harming your
                        credit score.
                    </StyledFaqText>

                    <StyledFaqText>
                        Acceptance isn’t guaranteed, but we use a soft search to
                        show your chance of acceptance for each card.
                    </StyledFaqText>

                    <StyledFaqText>
                        A soft search is a credit check you can see on your
                        report, but one lenders won’t use when assessing your
                        application, and they won’t affect your score.
                    </StyledFaqText>
                </StyledFaqContentStack>
            ),
        },

        {
            question: 'How can I improve my credit rating?',
            answer: (
                <StyledFaqContentStack>
                    <StyledFaqText>
                        Use the card. An unused card sitting in a drawer won’t
                        help your credit score. By using the card you’ll need to
                        make repayments, which will help your credit score.
                    </StyledFaqText>

                    <UnorderedList>
                        <ListItem>
                            Use less than 25% of your credit limit, and whatever
                            happens, always stay within the limit.
                        </ListItem>

                        <ListItem>
                            Set up a direct debit for at least the monthly
                            minimum amount, or ideally the full amount. This
                            will ensure you never miss a payment.
                        </ListItem>

                        <ListItem>
                            Never take on debt you can’t afford to pay off in
                            full. Credit-building cards tend to have high APRs
                            and expensive interest costs, which could lead to
                            you missing a repayment.
                        </ListItem>
                    </UnorderedList>
                </StyledFaqContentStack>
            ),
        },
    ],
    [CreditCardProductCategory.Purchase]: [
        ...generalScoredQuestions,
        {
            question: 'What is a 0% purchase card?',
            answer: (
                <StyledFaqContentStack>
                    <StyledFaqText>
                        A 0% purchase credit card enables you to spread the cost
                        of purchases, using an interest-free offer period,
                        during which you’ll pay no interest on the balance.
                    </StyledFaqText>

                    <StyledFaqText>
                        Plus, like any credit card, a 0% purchase card offers
                        section 75 protection on any purchase between £100 and
                        £30,000.
                    </StyledFaqText>

                    <StyledFaqText>
                        Make sure you pay your balance off before the offer
                        ends, otherwise you’ll pay interest on what’s left.
                    </StyledFaqText>
                </StyledFaqContentStack>
            ),
        },
    ],
    [CreditCardProductCategory.Rewards]: [
        ...generalScoredQuestions,
        {
            question: 'What is a rewards credit card?',
            answer: (
                <StyledFaqContentStack>
                    <StyledFaqText>
                        A rewards credit card helps you earn points which can be
                        exchanged for travel, merchandise or discounts.
                    </StyledFaqText>

                    <StyledFaqText>
                        Every time you purchase something, you will earn points.
                        They can be used to buy a whole host of things,
                        depending on that card’s individual rewards scheme.
                    </StyledFaqText>

                    <StyledFaqText>
                        Credit card companies aren’t just being generous.
                        They’re hoping that you use their card because of the
                        rewards and not clear your balance in full. They can
                        earn far more in interest than you will receive in
                        rewards.
                    </StyledFaqText>
                </StyledFaqContentStack>
            ),
        },

        {
            question: 'How do I get the most from my rewards card?',
            answer: (
                <StyledFaqContentStack>
                    <StyledFaqText>
                        Follow these tips to get the most from your reward card:
                    </StyledFaqText>

                    <UnorderedList>
                        <ListItem>
                            <b>Spend sensibly:</b> Think of it like your regular
                            debit card. Spend no more than you usually would and
                            remain in control to benefit from the rewards on
                            offer.
                        </ListItem>

                        <ListItem>
                            <b>Set up a direct debit:</b> Ensure you never miss
                            a payment, which may mean you lose any offers, by
                            setting up a monthly direct debit to repay your
                            balance in full.
                        </ListItem>
                    </UnorderedList>
                </StyledFaqContentStack>
            ),
        },

        {
            question: 'Will I be accepted for a rewards card?',
            answer: (
                <StyledFaqContentStack>
                    <StyledFaqText>
                        Used correctly, a rewards card can offer a lot of value,
                        but the acceptance criteria for them can be pretty
                        tight.
                    </StyledFaqText>

                    <StyledFaqText>
                        Each lender has their own acceptance criteria, so it’s
                        hard to know where to apply. You can check your
                        eligibility for any rewards card on TotallyMoney, giving
                        you the confidence to apply for the one that’s right for
                        you.
                    </StyledFaqText>
                </StyledFaqContentStack>
            ),
        },
    ],
    [CreditCardProductCategory.Travel]: [
        ...generalScoredQuestions,

        {
            question: 'What is a travel card?',
            answer: (
                <StyledFaqContentStack>
                    <StyledFaqText>
                        All credit cards offer the last four advantages
                        mentioned above, but not all are cheap. Many will charge
                        you significant extra fees if you use your card abroad.
                    </StyledFaqText>
                    <StyledFaqText>
                        In some cases you could be charged several pounds every
                        time you use your card. To get the cheapest possible
                        credit card you’ll need to get a credit card that is
                        specifically designed and marketed as being for use
                        abroad.
                    </StyledFaqText>
                    <StyledFaqText>
                        These cards don’t charge transaction fees on purchases
                        or foreign exchange fees, making them typically cheaper
                        to use than other credit or debit cards, helping you
                        save money when on holiday or when abroad for work.
                    </StyledFaqText>
                </StyledFaqContentStack>
            ),
        },

        {
            question: 'How can a travel card save me money abroad?',
            answer: (
                <StyledFaqContentStack>
                    <StyledFaqText>
                        A travel credit card is the best way to spend abroad,
                        for a number of reasons. For starters, they have no
                        transaction fees, and you get the best possible exchange
                        rate when you spend.
                    </StyledFaqText>
                    <StyledFaqText>
                        They’re also safer than cash, and you can claim refunds
                        on purchases gone wrong, just like you would at home.
                        Finally, they’re also flexible, allowing you access to
                        extra funds in case of an emergency.
                    </StyledFaqText>
                </StyledFaqContentStack>
            ),
        },

        {
            question: 'Tips and tricks for successful spending abroad',
            answer: (
                <StyledFaqContentStack>
                    <StyledFaqText>
                        Keep your bank informed Tell your bank where you’re
                        going before you leave, so they don’t flag “unusual”
                        transactions as fraud.
                    </StyledFaqText>
                    <StyledFaqContentStack>
                        <StyledFaqSubHeading>
                            Pay in local currency
                        </StyledFaqSubHeading>

                        <StyledFaqText>
                            To get the best exchange rate, always pay in local
                            currency on your card.
                        </StyledFaqText>
                    </StyledFaqContentStack>

                    <StyledFaqContentStack>
                        <StyledFaqSubHeading>
                            Be aware of pre-authorisation
                        </StyledFaqSubHeading>

                        <StyledFaqText>
                            To ensure you have available credit to pay your
                            bill, car hire companies and hotels reserve funds on
                            your card, called “pre-authorisation”. These never
                            leave your account, but it does impact your credit
                            limit.
                        </StyledFaqText>
                    </StyledFaqContentStack>

                    <StyledFaqContentStack>
                        <StyledFaqSubHeading>
                            Protect your card
                        </StyledFaqSubHeading>

                        <StyledFaqText>
                            Don’t let your card out of your sight, and don’t
                            give your PIN to anyone. Also, always shield your
                            PIN when typing it in.
                        </StyledFaqText>
                    </StyledFaqContentStack>
                </StyledFaqContentStack>
            ),
        },
    ],
}
